/**
 * Modul 1112 — HAB-AU ASSAY. Cockpit zlata.
 *
 * 1111 ostaje. Isti izvori (/api/ado-1112), druga stanica.
 * Vizual: LBMA Good Delivery + BullionVault bar-list + Open MCT conductor
 * + MetalDesk workstation. Mars habitat assay board, ne Stripe, ne 1111.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, downloadFile } from '../shared/api';
import {
    CompareLanes, GoldIngot, HatchArea, HatchSprite, HeatGrid, MixPlate, NetTank,
    RaceBars, Readout, TripleQuote, TypeSwatch, TYPE_COLOR, fmtKg, fmtOz,
    type TxnType,
} from './ado1112Charts';

type Locale = 'hr' | 'en';
type Channel = 'mp' | 'vele' | 'mjenjacnica' | 'zalog';
type Grain = 'offer' | 'article';
type Lens = 'toka' | 'teren' | 'usporedba' | 'sati' | 'smjesa';

type Kpi = {
    broj: number; eur: number; fine_g: number;
    eur_per_g: number | null; spot_eur_g: number | null; spread_pct: number | null;
    ruc_spot_eur?: number | null; ruc_fixing_eur?: number | null; spread_fixing_pct?: number | null;
    fixing_eur_g?: number | null;
    ponude?: number; prihvacene?: number; konverzija_pct?: number | null;
};
type FeedRow = {
    type: TxnType; id: number; ref: string; ts: string; accepted_ts: string | null;
    branch: string; country: string; subregion: string; group: string;
    artikl: string | null; metal: string | null; kolicina: number | null; fine_g: number | null;
    eur: number; eur_per_g: number | null; spot_eur_g: number | null; spread_pct: number | null;
    spread_fixing_pct?: number | null; fixing_eur_g?: number | null;
    status: string; source: string; partner: string | null; lines?: number; ruc_eur?: number | null;
};
type MetaBranch = { branch: string; country: string; subregion: string; group: string };
type MetaResp = {
    types: TxnType[]; branches: MetaBranch[];
    geo_tree: Record<string, Record<string, Record<string, string[]>>>;
    ranges: Record<TxnType, { min: string | null; max: string | null }>;
    channels?: Record<string, boolean>;
};
type ScopeTree = { tree: Record<string, Record<string, Record<string, string[]>>>; active: { group: string | null; country: string | null; subregion: string | null; branch: string | null } };
type Cockpit = {
    summary: { range: { from: string; to: string }; prodaja: Kpi; otkup_inv: Kpi; otkup_lom: Kpi; fixing_eur_g: number | null; grain: Grain; channel: Channel };
    spark: { labels: string[]; series: Record<string, number[]>; unit?: string };
    terrain: { rows: { branch: string; country: string; subregion: string; group: string; metrics: Record<string, { eur: number; broj?: number; fine_g?: number }> }[] };
    compare: { rows: { key: string; a: Record<string, { eur: number }>; b: Record<string, { eur: number }> | null }[]; a_range: { from: string; to: string } | null; b_range: { from: string; to: string } | null; monthly?: { key: string; a: Record<string, { eur: number }>; b: Record<string, { eur: number }> | null }[] };
    heat: { rows: { dow: number; hour: number; type: TxnType; broj: number; eur: number }[] };
    mix: { key: string; eur: number; fine_g: number; broj: number }[];
    scope: ScopeTree;
    tape: { rows: FeedRow[]; total: number; note?: string; aggregate?: boolean };
    channel: Channel;
    grain: Grain;
    _cache?: { cached_at: string; ttl: number; hit: boolean };
};
type ChainStep = { key: string; label: string; available: boolean; ts?: string | null; eur?: number | null; eur_per_g?: number | null; source?: string | null; spread_pct?: number | null; note?: string | null };
type DetailResp = { row: FeedRow; chain: ChainStep[] };

const ALL: TxnType[] = ['prodaja', 'otkup_inv', 'otkup_lom'];
const TYPE_HR: Record<TxnType, string> = { prodaja: 'Prodaja inv.', otkup_inv: 'Otkup inv.', otkup_lom: 'Otkup loma' };
const TYPE_EN: Record<TxnType, string> = { prodaja: 'Inv. sales', otkup_inv: 'Inv. buyback', otkup_lom: 'Scrap' };
const LENS_HR: Record<Lens, string> = { toka: 'Toka', teren: 'Teren', usporedba: 'Usporedba', sati: 'Sati', smjesa: 'Smjesa' };
const LENS_EN: Record<Lens, string> = { toka: 'Flow', teren: 'Ground', usporedba: 'Compare', sati: 'Hours', smjesa: 'Mix' };
const LENSES: Lens[] = ['toka', 'teren', 'usporedba', 'sati', 'smjesa'];

const nfEur = new Intl.NumberFormat('hr-HR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const nfEur2 = new Intl.NumberFormat('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nfG = new Intl.NumberFormat('hr-HR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const nfPct = new Intl.NumberFormat('hr-HR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function todayIso(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function isoDaysAgo(n: number): string {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function isoToHr(iso: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}
function hrToIso(hr: string): string | null {
    const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(hr.trim());
    if (!m) return null;
    return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}
function fmtEur(n: number, compact = false): string {
    return (compact ? nfEur : nfEur2).format(n) + ' €';
}
function fmtPct(n: number | null | undefined): string {
    if (n === null || n === undefined) return '—';
    return (n >= 0 ? '+' : '') + nfPct.format(n) + ' %';
}
function fmtTs(s: string, locale: Locale): string {
    const iso = s.replace(' ', 'T');
    const d = iso.slice(0, 10);
    const t = iso.slice(11, 16);
    return locale === 'hr' ? `${isoToHr(d)} ${t}` : `${d} ${t}`;
}
function pad2(n: number): string { return String(n).padStart(2, '0'); }
function fmtClock(d: Date, locale: Locale): string {
    const t = `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
    if (locale === 'hr') return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ${t}`;
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${t}`;
}

type Filters = {
    dateFrom: string; dateTo: string; types: TxnType[];
    group: string; country: string; subregion: string; branch: string;
    grain: Grain; channel: Channel; q: string;
};

function defaultFilters(): Filters {
    return {
        dateFrom: isoDaysAgo(30), dateTo: todayIso(), types: [...ALL],
        group: '', country: '', subregion: '', branch: '',
        grain: 'offer', channel: 'mp', q: '',
    };
}

function qsOf(f: Filters, extra?: Record<string, string>): string {
    const p = new URLSearchParams();
    p.set('date_from', f.dateFrom);
    p.set('date_to', f.dateTo);
    p.set('types', f.types.join(','));
    p.set('grain', f.grain);
    p.set('channel', f.channel);
    p.set('sale_basis', 'prihvat');
    if (f.group) p.set('group', f.group);
    if (f.country) p.set('country', f.country);
    if (f.subregion) p.set('subregion', f.subregion);
    if (f.branch) p.set('branches', f.branch);
    if (f.q) p.set('q', f.q);
    if (extra) for (const [k, v] of Object.entries(extra)) p.set(k, v);
    return p.toString();
}

const CSS = `
.a12-root{position:fixed;inset:0;z-index:4000;color:#f4ead8;overflow:auto;
  font-family:"Liberation Sans Narrow","DejaVu Sans Condensed","Segoe UI Narrow",Arial,sans-serif;
  background:#060403;
  background-image:
    radial-gradient(ellipse at 8% -8%, #3a1c0c 0%, transparent 42%),
    radial-gradient(circle at 92% 108%, #1a0c08 0%, transparent 38%),
    repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,.18) 3px);
}
.a12-root *{box-sizing:border-box;}
.a12-root button,.a12-root input{font:inherit;color:inherit;}
.a12-root ::selection{background:#f0c14b;color:#0a0704;}
.a12-root::-webkit-scrollbar,.a12-root *::-webkit-scrollbar{width:10px;height:10px;}
.a12-root::-webkit-scrollbar-thumb,.a12-root *::-webkit-scrollbar-thumb{background:#3a2e22;border:2px solid #060403;}
.a12-hud{position:sticky;top:0;z-index:6;background:rgba(6,4,3,.94);
  border-bottom:1px solid #3a2e22; box-shadow:0 1px 0 #9a623433;
  padding:8px 14px 6px; display:grid; gap:8px;}
.a12-hud-top{display:flex;align-items:center;gap:12px;flex-wrap:wrap;}
.a12-stamp{display:flex;align-items:center;gap:10px;}
.a12-cartouche{font-family:"Liberation Serif",Palatino,"Times New Roman",serif;
  font-size:20px;letter-spacing:4px;color:#f0c14b;border:1px solid #f0c14b;
  padding:1px 10px 0; line-height:1.35; background:linear-gradient(#1a1208,#0e0a07);}
.a12-stamp-meta{display:flex;flex-direction:column;gap:0;font-family:"Liberation Mono",ui-monospace,monospace;font-size:10px;letter-spacing:1.6px;color:#8a7a66;}
.a12-stamp-meta strong{color:#f0c14b;font-weight:400;letter-spacing:2px;}
.a12-clock{margin-left:auto;font-family:"Liberation Mono",ui-monospace,monospace;font-size:13px;color:#f0c14b;letter-spacing:1px;
  border:1px solid #3a2e22;padding:4px 10px;background:#0e0a07;}
.a12-clock em{color:#8a7a66;font-style:normal;margin-right:8px;letter-spacing:2px;font-size:10px;}
.a12-row{display:flex;align-items:center;gap:6px;flex-wrap:wrap;}
.a12-chip{background:transparent;border:1px solid #3a2e22;color:#cbbba6;padding:4px 9px;font-size:11px;letter-spacing:.8px;cursor:pointer;}
.a12-chip.is-on{background:#f0c14b;color:#0a0704;border-color:#f0c14b;}
.a12-seg{display:flex;border:1px solid #f0c14b;}
.a12-seg button{background:transparent;border:0;color:#f0c14b;padding:5px 12px;cursor:pointer;letter-spacing:1.4px;font-size:11px;}
.a12-seg button.is-on{background:#f0c14b;color:#0a0704;font-weight:700;}
.a12-q{background:#0e0a07;border:1px solid #3a2e22;color:#f4ead8;padding:5px 8px;font-family:"Liberation Mono",ui-monospace,monospace;font-size:12px;min-width:180px;}
.a12-iconbtn{background:transparent;border:1px solid #3a2e22;color:#cbbba6;padding:5px 10px;cursor:pointer;letter-spacing:1px;font-size:11px;}
.a12-iconbtn:disabled{opacity:.45;}
.a12-keys{font-family:"Liberation Mono",ui-monospace,monospace;font-size:10px;color:#5e5348;letter-spacing:.6px;}
.a12-quote{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:8px;padding:10px 14px 0;}
.a12-qcell{background:#0e0a07;border:1px solid #3a2e22;box-shadow:inset 0 0 0 1px #060403, 0 0 0 1px #9a623422;padding:8px 10px;}
.a12-qcell .k{font-size:10px;letter-spacing:1.8px;color:#8a7a66;}
.a12-qcell .v{font-family:"Liberation Mono",ui-monospace,monospace;font-size:18px;color:#f0c14b;margin-top:2px;font-variant-numeric:tabular-nums;}
.a12-qcell .s{font-family:"Liberation Mono",ui-monospace,monospace;font-size:11px;color:#8a7a66;margin-top:2px;}
.a12-qcell.pos .v{color:#6fc07a;} .a12-qcell.neg .v{color:#d45a3a;}
.a12-body{display:grid;grid-template-columns:220px minmax(0,1fr);gap:12px;padding:12px 14px 24px;align-items:start;}
.a12-plate{background:#110c09;border:1px solid #3a2e22;box-shadow:inset 0 0 0 1px #060403, 0 0 0 1px #9a623428;padding:12px;}
.a12-scope h2,.a12-viz h2,.a12-tape-h{font-size:11px;letter-spacing:2.2px;color:#f0c14b;margin:0 0 8px;font-weight:400;}
.a12-scope{display:grid;gap:8px;}
.a12-crumb{font-family:"Liberation Mono",ui-monospace,monospace;font-size:10px;color:#8a7a66;letter-spacing:.4px;}
.a12-tree{display:grid;gap:3px;max-height:58vh;overflow:auto;}
.a12-tree button{text-align:left;background:transparent;border:1px solid transparent;padding:4px 8px;cursor:pointer;font-size:12px;color:#cbbba6;}
.a12-tree button.is-on{border-color:#f0c14b;color:#0a0704;background:#f0c14b;}
.a12-tree .lvl2{padding-left:14px;border-left:2px solid #9a6234;margin-left:4px;}
.a12-tree .lvl3{padding-left:14px;border-left:2px solid #3a2e22;margin-left:12px;}
.a12-tree .lvl4{padding-left:12px;margin-left:20px;font-family:"Liberation Mono",ui-monospace,monospace;font-size:11px;}
.a12-ingots{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;}
.a12-ingot{display:flex;gap:8px;align-items:center;background:#110c09;border:1px solid #3a2e22;padding:8px;cursor:pointer;text-align:left;width:100%;opacity:.45;}
.a12-ingot.is-on{opacity:1;border-color:var(--a12-ty,#f0c14b);}
.a12-ingot-iso{flex-shrink:0;}
.a12-ingot-meta{min-width:0;flex:1;}
.a12-ingot-meta .a12-k{font-size:10px;letter-spacing:1.4px;color:#8a7a66;}
.a12-ingot-meta .a12-eur{font-family:"Liberation Mono",ui-monospace,monospace;font-size:16px;color:#f4ead8;}
.a12-ingot-meta .a12-oz{font-size:11px;color:#8a7a66;font-family:"Liberation Mono",ui-monospace,monospace;}
.a12-ingot-meta .a12-dual{display:flex;flex-wrap:wrap;gap:6px;margin-top:4px;font-family:"Liberation Mono",ui-monospace,monospace;font-size:10px;color:#8a7a66;}
.a12-side{display:grid;gap:10px;}
.a12-tank .a12-tank-lab,.a12-triple-lab{font-size:10px;letter-spacing:1.8px;color:#8a7a66;margin-bottom:6px;}
.a12-tank-track{display:flex;height:14px;background:#0e0a07;border:1px solid #3a2e22;}
.a12-tank-sold{background:repeating-linear-gradient(-45deg,#f0c14b 0 2px,transparent 2px 5px);opacity:.9;}
.a12-tank-bought{background:repeating-linear-gradient(0deg,#3db8a8 0 2px,transparent 2px 6px);}
.a12-tank-nums{display:flex;justify-content:space-between;font-family:"Liberation Mono",ui-monospace,monospace;font-size:11px;margin-top:6px;color:#8a7a66;}
.a12-tank-nums strong{color:#f0c14b;} .a12-tank-nums strong.neg{color:#d45a3a;}
.a12-tank-oz{font-family:"Liberation Mono",ui-monospace,monospace;font-size:10px;color:#5e5348;margin-top:2px;}
.a12-triple-rail{position:relative;height:16px;background:#0e0a07;border:1px solid #3a2e22;}
.a12-triple-rail i{position:absolute;top:0;bottom:0;width:2px;margin-left:-1px;}
.a12-triple-rail .n-spot{background:#8a7a66;}
.a12-triple-rail .n-fix{background:#9a6234;}
.a12-triple-rail .n-real{background:#f0c14b;width:3px;}
.a12-triple-nums{display:flex;justify-content:space-between;font-family:"Liberation Mono",ui-monospace,monospace;font-size:11px;margin-top:4px;}
.a12-triple-nums .real{color:#f0c14b;} .a12-triple-nums .spot{color:#8a7a66;} .a12-triple-nums .fix{color:#9a6234;}
.a12-main{display:grid;gap:12px;min-width:0;}
.a12-viz-h{display:flex;gap:6px;flex-wrap:wrap;align-items:center;margin-bottom:8px;}
.a12-cache{margin-left:auto;font-family:"Liberation Mono",ui-monospace,monospace;font-size:10px;color:#5e5348;}
.a12-err{color:#d45a3a;font-size:13px;margin-bottom:8px;}
.a12-load{color:#8a7a66;font-family:"Liberation Mono",ui-monospace,monospace;letter-spacing:2px;padding:24px 0;text-align:center;}
.a12-chart{display:block;cursor:crosshair;}
.a12-race{display:grid;gap:5px;}
.a12-race-row{display:grid;grid-template-columns:22px 1fr 92px;gap:8px;align-items:center;background:transparent;border:1px solid transparent;padding:3px 6px;cursor:pointer;text-align:left;}
.a12-race-row.is-on{background:#1c1610;border-color:#f0c14b;}
.a12-race-n{font-family:"Liberation Mono",ui-monospace,monospace;font-size:11px;color:#8a7a66;}
.a12-race-name{font-size:12px;color:#f4ead8;} .a12-race-name em{display:block;font-style:normal;font-size:10px;color:#8a7a66;}
.a12-race-track{margin-top:4px;height:12px;background:#0e0a07;}
.a12-race-val{font-family:"Liberation Mono",ui-monospace,monospace;text-align:right;color:#f0c14b;font-size:12px;}
.a12-race-val span{display:block;color:#8a7a66;font-size:10px;}
.a12-mix{display:flex;gap:16px;align-items:center;}
.a12-mix-legend{display:grid;gap:6px;flex:1;}
.a12-mix-row{display:grid;grid-template-columns:14px 1fr auto auto;gap:8px;align-items:center;background:transparent;border:1px solid #2a241c;padding:6px 8px;cursor:pointer;}
.a12-mix-row.is-on{border-color:#f0c14b;}
.a12-mix-k{font-size:12px;} .a12-mix-v{font-family:"Liberation Mono",ui-monospace,monospace;color:#f0c14b;} .a12-mix-g{font-family:"Liberation Mono",ui-monospace,monospace;color:#8a7a66;font-size:11px;}
.a12-readout{background:#0e0a07;border:1px solid #f0c14b;padding:10px 28px 10px 12px;font-family:"Liberation Mono",ui-monospace,monospace;font-size:12px;color:#f4ead8;display:grid;gap:3px;position:relative;margin-top:10px;}
.a12-readout-x{position:absolute;top:4px;right:6px;background:none;border:none;color:#8a7a66;cursor:pointer;}
.a12-tape{padding:0;}
.a12-tape-h{display:flex;justify-content:space-between;padding:8px 12px;border-bottom:1px solid #3a2e22;}
.a12-tape-note{padding:8px 12px;color:#8a7a66;font-size:12px;}
.a12-tape-grid{display:grid;grid-template-columns:118px 92px 1.15fr 1.55fr 78px 92px 72px 68px 68px;gap:0;}
.a12-tape-head{padding:6px 12px;border-bottom:1px solid #3a2e22;font-size:10px;letter-spacing:1px;color:#8a7a66;text-transform:uppercase;}
.a12-tape-row{width:100%;padding:7px 12px;background:transparent;border:0;border-bottom:1px solid #241c16;color:#f4ead8;text-align:left;cursor:pointer;}
.a12-tape-row:nth-child(even){background:#0c0907;}
.a12-tape-row:hover{background:#1c1610;}
.a12-mono{font-family:"Liberation Mono",ui-monospace,monospace;font-variant-numeric:tabular-nums;}
.a12-right{text-align:right;}
.a12-mute{color:#8a7a66;} .a12-pos{color:#6fc07a;} .a12-neg{color:#d45a3a;}
.a12-more{width:100%;padding:10px;background:transparent;border:0;color:#f0c14b;letter-spacing:1.4px;cursor:pointer;}
.a12-scrim{position:fixed;inset:0;background:rgba(6,4,3,.78);z-index:4500;}
.a12-cert{position:fixed;top:0;right:0;bottom:0;width:min(460px,94vw);background:#110c09;border-left:1px solid #9a6234;z-index:4501;display:flex;flex-direction:column;color:#f4ead8;}
.a12-cert-h{padding:14px;border-bottom:1px solid #3a2e22;display:flex;align-items:center;gap:10px;}
.a12-cert-b{flex:1;overflow:auto;padding:16px;}
.a12-step{display:grid;grid-template-columns:16px 1fr;gap:10px;margin-bottom:12px;}
.a12-dot{width:12px;height:12px;border-radius:50%;margin-top:3px;background:#3a2e22;}
.a12-dot.on{background:#f0c14b;}
.a12-hrdate{width:108px;background:#0e0a07;color:#f4ead8;border:1px solid #3a2e22;font-family:"Liberation Mono",ui-monospace,monospace;font-size:12px;padding:5px 6px;letter-spacing:.4px;}
.a12-ticker{overflow:hidden;border-top:1px solid #241c16;background:#0a0705;height:22px;}
.a12-ticker i{display:inline-block;white-space:nowrap;animation:a12mar 42s linear infinite;font-family:"Liberation Mono",ui-monospace,monospace;font-size:11px;color:#8a7a66;padding:4px 0;}
@keyframes a12mar{from{transform:translateX(0)}to{transform:translateX(-50%)}}
@media (max-width:1100px){
  .a12-body{grid-template-columns:1fr;}
  .a12-quote{grid-template-columns:repeat(3,minmax(0,1fr));}
  .a12-ingots{grid-template-columns:1fr;}
}
`;

function HrDate({ value, onChange, min, max }: { value: string; onChange: (iso: string) => void; min?: string; max?: string }) {
    const [txt, setTxt] = useState(isoToHr(value));
    useEffect(() => { setTxt(isoToHr(value)); }, [value]);
    return (
        <input
            className="a12-hrdate"
            value={txt}
            placeholder="dd.mm.yyyy"
            onChange={(e) => setTxt(e.target.value)}
            onBlur={() => {
                const iso = hrToIso(txt);
                if (iso && (!min || iso >= min) && (!max || iso <= max)) onChange(iso);
                else setTxt(isoToHr(value));
            }}
        />
    );
}

function ScopePlate({ meta, filters, set }: {
    meta: MetaResp | null; filters: Filters; set: (p: Partial<Filters>) => void;
}) {
    const tree = meta?.geo_tree ?? {};
    const groups = Object.keys(tree).sort();
    const countries = filters.group && tree[filters.group] ? Object.keys(tree[filters.group]).sort() : [];
    const subregions = filters.group && filters.country && tree[filters.group]?.[filters.country]
        ? Object.keys(tree[filters.group][filters.country]).sort() : [];
    const branches = filters.group && filters.country && filters.subregion
        ? (tree[filters.group]?.[filters.country]?.[filters.subregion] ?? []).slice().sort()
        : [];
    const crumb = ['MREŽA', filters.group, filters.country, filters.subregion, filters.branch].filter(Boolean).join(' / ');

    return (
        <aside className="a12-plate a12-scope">
            <h2>OPSEG · VAULT</h2>
            <div className="a12-row">
                {([
                    ['mp', 'MP'],
                    ['vele', 'VELE'],
                    ['mjenjacnica', 'MJENJAČ'],
                    ['zalog', 'ZALOG'],
                ] as [Channel, string][]).map(([k, l]) => (
                    <button key={k} type="button" className={'a12-chip' + (filters.channel === k ? ' is-on' : '')} onClick={() => set({ channel: k })}>{l}</button>
                ))}
            </div>
            <div className="a12-crumb">{crumb}</div>
            <div className="a12-tree">
                <button type="button" className={!filters.group && !filters.branch ? 'is-on' : ''} onClick={() => set({ group: '', country: '', subregion: '', branch: '' })}>MREŽA</button>
                {groups.map((g) => (
                    <button key={g} type="button" className={filters.group === g && !filters.country ? 'is-on' : ''} onClick={() => set({ group: g, country: '', subregion: '', branch: '' })}>{g}</button>
                ))}
                {countries.map((c) => (
                    <button key={c} type="button" className={'lvl2' + (filters.country === c && !filters.subregion ? ' is-on' : '')} onClick={() => set({ country: c, subregion: '', branch: '' })}>{c}</button>
                ))}
                {subregions.map((s) => (
                    <button key={s} type="button" className={'lvl3' + (filters.subregion === s && !filters.branch ? ' is-on' : '')} onClick={() => set({ subregion: s, branch: '' })}>{s}</button>
                ))}
                {branches.map((b) => (
                    <button key={b} type="button" className={'lvl4' + (filters.branch === b ? ' is-on' : '')} onClick={() => set({ branch: b })}>{b}</button>
                ))}
            </div>
        </aside>
    );
}

const COLS: { key: string; hr: string; en: string; hintHr: string; hintEn: string; align?: 'right' }[] = [
    { key: 'ts', hr: 'Vrijeme', en: 'Time', hintHr: 'Prihvat (prodaja) ili transakcija (otkup). dd.mm.yyyy HH:mm.', hintEn: 'Acceptance (sales) or transaction (buyback).' },
    { key: 'type', hr: 'Tok', en: 'Flow', hintHr: 'Prodaja inv. / otkup inv. / otkup loma.', hintEn: 'Investment sale / buyback / scrap.' },
    { key: 'place', hr: 'Sef', en: 'Vault', hintHr: 'Poslovnica i država.', hintEn: 'Branch and country.' },
    { key: 'ref', hr: 'Serija', en: 'Serial', hintHr: 'Broj ponude ili dokument. Drugi red: artikl.', hintEn: 'Offer/document. Second line is the article.' },
    { key: 'fine', hr: 'Fine g', en: 'Fine g', hintHr: 'Fini grami zlata.', hintEn: 'Fine grams of gold.', align: 'right' },
    { key: 'eur', hr: 'EUR', en: 'EUR', hintHr: 'Iznos u eurima.', hintEn: 'Amount in EUR.', align: 'right' },
    { key: 'eurg', hr: 'EUR/g', en: 'EUR/g', hintHr: 'Cijena po finom gramu.', hintEn: 'Price per fine gram.', align: 'right' },
    { key: 'spot', hr: 'Δ spot', en: 'Δ spot', hintHr: 'EUR/g vs spot. Plus na prodaji = iznad spota.', hintEn: 'EUR/g vs spot.', align: 'right' },
    { key: 'fix', hr: 'Δ fixing', en: 'Δ fixing', hintHr: 'EUR/g vs Argor fixing u rasponu.', hintEn: 'EUR/g vs Argor fixing.', align: 'right' },
];

function Tape({ rows, locale, total, loading, note, onOpen, onMore, hasMore }: {
    rows: FeedRow[]; locale: Locale; total: number; loading: boolean; note?: string;
    onOpen: (r: FeedRow) => void; onMore: () => void; hasMore: boolean;
}) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    return (
        <section className="a12-plate a12-tape">
            <div className="a12-tape-h">
                <span>BAR LIST · {t('TRAKA', 'TAPE')}</span>
                <span className="a12-mono a12-mute">{rows.length} / {total}</span>
            </div>
            {note ? <div className="a12-tape-note">{note}</div> : null}
            <div className="a12-tape-grid a12-tape-head">
                {COLS.map((c) => (
                    <div key={c.key} title={locale === 'hr' ? c.hintHr : c.hintEn} className={c.align === 'right' ? 'a12-right' : undefined} style={{ cursor: 'help' }}>
                        {locale === 'hr' ? c.hr : c.en}
                    </div>
                ))}
            </div>
            {rows.map((r) => (
                <button
                    key={r.type + ':' + r.id + ':' + r.ref}
                    type="button"
                    className="a12-tape-grid a12-tape-row"
                    onClick={() => onOpen(r)}
                >
                    <span className="a12-mono" style={{ fontSize: 11 }}>{fmtTs(r.ts, locale)}</span>
                    <span style={{ fontSize: 11, color: TYPE_COLOR[r.type], display: 'flex', alignItems: 'center', gap: 6 }}>
                        <TypeSwatch type={r.type} size={10} />{locale === 'hr' ? TYPE_HR[r.type] : TYPE_EN[r.type]}
                    </span>
                    <span style={{ fontSize: 12 }}>
                        {r.branch}
                        <div className="a12-mute" style={{ fontSize: 10 }}>{r.country}{r.subregion !== '-' ? ' · ' + r.subregion : ''}</div>
                    </span>
                    <span style={{ fontSize: 12, minWidth: 0 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.ref}{(r.lines ?? 1) > 1 ? ` · ${r.lines}` : ''}</div>
                        <div className="a12-mute" style={{ fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.artikl ?? ''}{r.partner ? ' · ' + r.partner : ''}</div>
                    </span>
                    <span className="a12-mono a12-right" style={{ fontSize: 12 }}>{r.fine_g != null ? nfG.format(r.fine_g) : '—'}</span>
                    <span className="a12-mono a12-right" style={{ fontSize: 12 }}>{fmtEur(r.eur, true)}</span>
                    <span className="a12-mono a12-right a12-mute" style={{ fontSize: 11 }}>{r.eur_per_g != null ? nfEur2.format(r.eur_per_g) : '—'}</span>
                    <span className={'a12-mono a12-right ' + ((r.spread_pct ?? 0) >= 0 ? 'a12-pos' : 'a12-neg')} style={{ fontSize: 11 }}>{fmtPct(r.spread_pct)}</span>
                    <span className={'a12-mono a12-right ' + ((r.spread_fixing_pct ?? 0) >= 0 ? 'a12-pos' : 'a12-neg')} style={{ fontSize: 11 }}>{fmtPct(r.spread_fixing_pct ?? null)}</span>
                </button>
            ))}
            {loading ? <div className="a12-load">{t('ASSAY…', 'ASSAY…')}</div> : null}
            {hasMore && !loading ? (
                <button type="button" className="a12-more" onClick={onMore}>{t('JOŠ REDOVA', 'MORE ROWS')}</button>
            ) : null}
        </section>
    );
}

function Detail({ token, locale, target, onClose }: {
    token: string | null; locale: Locale; target: { type: TxnType; id: number }; onClose: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const [data, setData] = useState<DetailResp | null>(null);
    const [err, setErr] = useState<string | null>(null);
    useEffect(() => {
        let alive = true;
        setData(null); setErr(null);
        if (target.id <= 0) { setErr(t('Nema stavke za detalj (agregat).', 'No detail row (aggregate).')); return; }
        api<DetailResp>(`/ado-1112/detail?type=${target.type}&id=${target.id}`, token)
            .then((d) => { if (alive) setData(d); })
            .catch((e: Error) => { if (alive) setErr(e.message); });
        return () => { alive = false; };
    }, [token, target, locale]);

    return (
        <>
            <div className="a12-scrim" onClick={onClose} />
            <aside className="a12-cert">
                <div className="a12-cert-h">
                    <TypeSwatch type={target.type} />
                    <span style={{ letterSpacing: 2, color: TYPE_COLOR[target.type] }}>{locale === 'hr' ? TYPE_HR[target.type] : TYPE_EN[target.type]}</span>
                    <span className="a12-mute" style={{ fontSize: 11, letterSpacing: 2 }}>CERT 999.9</span>
                    <button type="button" className="a12-iconbtn" style={{ marginLeft: 'auto' }} onClick={onClose}>ESC</button>
                </div>
                <div className="a12-cert-b">
                    {err ? <div className="a12-err">{err}</div> : null}
                    {!data && !err ? <div className="a12-load">{t('LANAC…', 'CHAIN…')}</div> : null}
                    {data ? (
                        <>
                            <div className="a12-mono a12-mute" style={{ fontSize: 12 }}>{data.row.ref} · {fmtTs(data.row.ts, locale)}</div>
                            <div style={{ fontSize: 16, margin: '6px 0' }}>{data.row.artikl ?? '—'}</div>
                            <div className="a12-mute" style={{ fontSize: 12, marginBottom: 6 }}>{data.row.branch} · {data.row.country}</div>
                            {data.row.fine_g != null ? (
                                <div className="a12-mono" style={{ marginBottom: 14, color: '#f0c14b' }}>
                                    {fmtKg(data.row.fine_g)} · {fmtOz(data.row.fine_g)} · {fmtEur(data.row.eur)}
                                </div>
                            ) : null}
                            <div style={{ letterSpacing: 2, color: '#f0c14b', marginBottom: 10 }}>{t('LANAC CIJENE', 'PRICE CHAIN')}</div>
                            {data.chain.map((s) => (
                                <div key={s.key} className="a12-step">
                                    <div className={'a12-dot' + (s.available ? ' on' : '')} />
                                    <div>
                                        <div style={{ color: s.available ? '#f4ead8' : '#8a7a66' }}>{s.label}</div>
                                        {s.available ? (
                                            <div className="a12-mono a12-mute" style={{ fontSize: 12, display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                                                {s.eur != null ? <span>{fmtEur(s.eur)}</span> : null}
                                                {s.eur_per_g != null ? <span>{nfEur2.format(s.eur_per_g)} €/g</span> : null}
                                                {s.spread_pct != null ? <span>{fmtPct(s.spread_pct)}</span> : null}
                                                {s.ts ? <span>{fmtTs(s.ts, locale)}</span> : null}
                                            </div>
                                        ) : null}
                                        {s.note ? <div className="a12-mute" style={{ fontSize: 11, marginTop: 4 }}>{s.note}</div> : null}
                                    </div>
                                </div>
                            ))}
                        </>
                    ) : null}
                </div>
            </aside>
        </>
    );
}

export function Ado1112Module({ token, locale, onExit }: { token: string | null; locale: Locale; onExit: () => void }) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const [filters, setFilters] = useState<Filters>(defaultFilters);
    const [meta, setMeta] = useState<MetaResp | null>(null);
    const [cockpit, setCockpit] = useState<Cockpit | null>(null);
    const [tapeRows, setTapeRows] = useState<FeedRow[]>([]);
    const [tapeTotal, setTapeTotal] = useState(0);
    const [tapePage, setTapePage] = useState(1);
    const [err, setErr] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [fresh, setFresh] = useState(0);
    const [lens, setLens] = useState<Lens>('toka');
    const [pick, setPick] = useState<ReactNode>(null);
    const [detail, setDetail] = useState<{ type: TxnType; id: number } | null>(null);
    const [exporting, setExporting] = useState(false);
    const [now, setNow] = useState(() => new Date());
    const searchRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const id = window.setInterval(() => setNow(new Date()), 1000);
        return () => window.clearInterval(id);
    }, []);

    const patch = (p: Partial<Filters>) => { setFresh(0); setFilters((f) => ({ ...f, ...p })); };
    const qs = useMemo(() => qsOf(filters) + (fresh > 0 ? `&fresh=1&rf=${fresh}` : ''), [filters, fresh]);

    useEffect(() => {
        api<MetaResp>('/ado-1112/meta', token).then(setMeta).catch(() => { /* meta optional */ });
    }, [token]);

    const toggleType = (ty: TxnType) => {
        const has = filters.types.includes(ty);
        const next = has ? filters.types.filter((x) => x !== ty) : [...filters.types, ty];
        patch({ types: next.length ? next : [...ALL] });
    };

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement | null)?.tagName;
            const typing = tag === 'INPUT' || tag === 'TEXTAREA';
            if (e.key === 'Escape') { if (detail) { setDetail(null); e.preventDefault(); } else onExit(); return; }
            if (typing) return;
            if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); return; }
            if (e.key === 'g' || e.key === 'G') { patch({ grain: filters.grain === 'offer' ? 'article' : 'offer' }); return; }
            if (e.key === 't' || e.key === 'T') { patch({ dateFrom: todayIso(), dateTo: todayIso() }); return; }
            if (e.key === 'r' || e.key === 'R') { setFresh((n) => n + 1); return; }
            if (e.key >= '1' && e.key <= '5') { setLens(LENSES[Number(e.key) - 1] ?? 'toka'); setPick(null); return; }
            if (e.key === 'q' || e.key === 'Q') {
                setFilters((f) => {
                    const has = f.types.includes('prodaja');
                    const next: TxnType[] = has ? f.types.filter((x) => x !== 'prodaja') : [...f.types, 'prodaja'];
                    return { ...f, types: next.length ? next : [...ALL] };
                });
            }
            if (e.key === 'w' || e.key === 'W') {
                setFilters((f) => {
                    const has = f.types.includes('otkup_inv');
                    const next: TxnType[] = has ? f.types.filter((x) => x !== 'otkup_inv') : [...f.types, 'otkup_inv'];
                    return { ...f, types: next.length ? next : [...ALL] };
                });
            }
            if (e.key === 'e' || e.key === 'E') {
                setFilters((f) => {
                    const has = f.types.includes('otkup_lom');
                    const next: TxnType[] = has ? f.types.filter((x) => x !== 'otkup_lom') : [...f.types, 'otkup_lom'];
                    return { ...f, types: next.length ? next : [...ALL] };
                });
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onExit, detail, filters.grain]);

    useEffect(() => {
        let alive = true;
        setLoading(true); setErr(null); setPick(null);
        api<Cockpit>(`/ado-1112/cockpit?${qs}`, token)
            .then((c) => {
                if (!alive) return;
                setCockpit(c);
                setTapeRows(c.tape.rows);
                setTapeTotal(c.tape.total);
                setTapePage(1);
            })
            .catch((e: Error) => { if (alive) setErr(e.message); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [token, qs]);

    const loadMore = useCallback(() => {
        const next = tapePage + 1;
        setLoading(true);
        api<Cockpit['tape']>(`/ado-1112/feed?${qs}&page=${next}&per_page=40`, token)
            .then((f) => { setTapeRows((p) => [...p, ...f.rows]); setTapePage(next); setTapeTotal(f.total); })
            .catch((e: Error) => setErr(e.message))
            .finally(() => setLoading(false));
    }, [token, qs, tapePage]);

    const presets = useMemo(() => {
        const y = todayIso().slice(0, 4);
        const m = todayIso().slice(0, 7);
        return [
            { label: t('Danas', 'Today'), from: todayIso(), to: todayIso() },
            { label: '7d', from: isoDaysAgo(7), to: todayIso() },
            { label: '30d', from: isoDaysAgo(30), to: todayIso() },
            { label: 'MTD', from: `${m}-01`, to: todayIso() },
            { label: 'YTD', from: `${y}-01-01`, to: todayIso() },
        ];
    }, [locale]);

    const kpi = (ty: TxnType): Kpi | null => (cockpit ? cockpit.summary[ty] : null);

    const raceRows = useMemo(() => {
        return (cockpit?.terrain.rows ?? []).map((r) => ({
            key: r.branch,
            sub: `${r.country} · ${r.subregion}`,
            value: r.metrics.ukupno?.eur ?? 0,
            g: r.metrics.ukupno?.fine_g ?? 0,
            broj: r.metrics.ukupno?.broj ?? 0,
            parts: ALL.filter((ty) => filters.types.includes(ty)).map((ty) => ({ ty, v: r.metrics[ty]?.eur ?? 0 })),
        }));
    }, [cockpit, filters.types]);

    const compareSeries = useMemo(() => {
        const rows = cockpit?.compare.monthly?.length ? cockpit.compare.monthly : (cockpit?.compare.rows ?? []);
        const labels = rows.map((r) => r.key);
        const a: number[] = [];
        const b: number[] = [];
        for (const r of rows) {
            a.push(ALL.filter((ty) => filters.types.includes(ty)).reduce((s, ty) => s + (r.a?.[ty]?.eur ?? 0), 0));
            b.push(ALL.filter((ty) => filters.types.includes(ty)).reduce((s, ty) => s + (r.b?.[ty]?.eur ?? 0), 0));
        }
        return { labels, a, b };
    }, [cockpit, filters.types]);

    const minDate = useMemo(() => {
        const mins = meta ? Object.values(meta.ranges).map((r) => r.min).filter((v): v is string => !!v) : [];
        return mins.length ? mins.sort()[0].slice(0, 10) : '2020-01-01';
    }, [meta]);

    const soldG = cockpit?.summary.prodaja.fine_g ?? 0;
    const boughtG = (cockpit?.summary.otkup_inv.fine_g ?? 0) + (cockpit?.summary.otkup_lom.fine_g ?? 0);
    const soldEur = cockpit?.summary.prodaja.eur ?? 0;
    const boughtEur = (cockpit?.summary.otkup_inv.eur ?? 0) + (cockpit?.summary.otkup_lom.eur ?? 0);
    const realized = cockpit?.summary.prodaja.eur_per_g ?? null;
    const spot = cockpit?.summary.prodaja.spot_eur_g ?? null;
    const fixing = cockpit?.summary.fixing_eur_g ?? null;
    const spread = cockpit?.summary.prodaja.spread_pct ?? null;
    const txnN = ALL.reduce((s, ty) => s + (kpi(ty)?.broj ?? 0), 0);

    const ticker = tapeRows.slice(0, 12).map((r) => (
        `${fmtTs(r.ts, locale)}  ${locale === 'hr' ? TYPE_HR[r.type] : TYPE_EN[r.type]}  ${r.branch}  ${r.fine_g != null ? fmtKg(r.fine_g) : ''}  ${fmtEur(r.eur, true)}`
    )).join('    ·    ');

    return (
        <div className="a12-root">
            <style>{CSS}</style>
            <HatchSprite />
            <header className="a12-hud">
                <div className="a12-hud-top">
                    <div className="a12-stamp">
                        <span className="a12-cartouche">ASSAY</span>
                        <span className="a12-stamp-meta">
                            <strong>HAB-AU · 1112</strong>
                            <span>FINENESS 999.9 · {t('PRIHVAT', 'ACCEPTED')}</span>
                        </span>
                    </div>
                    <div className="a12-row">
                        <HrDate value={filters.dateFrom} min={minDate} max={filters.dateTo} onChange={(iso) => patch({ dateFrom: iso })} />
                        <span className="a12-mute">→</span>
                        <HrDate value={filters.dateTo} min={filters.dateFrom} max={todayIso()} onChange={(iso) => patch({ dateTo: iso })} />
                        {presets.map((p) => {
                            const on = filters.dateFrom === p.from && filters.dateTo === p.to;
                            return (
                                <button key={p.label} type="button" className={'a12-chip' + (on ? ' is-on' : '')} onClick={() => patch({ dateFrom: p.from, dateTo: p.to })}>{p.label}</button>
                            );
                        })}
                    </div>
                    <div className="a12-seg">
                        {(['offer', 'article'] as Grain[]).map((g) => (
                            <button key={g} type="button" className={filters.grain === g ? 'is-on' : ''} onClick={() => patch({ grain: g })}>
                                {g === 'offer' ? t('PONUDA', 'OFFER') : t('ARTIKL', 'ARTICLE')}
                            </button>
                        ))}
                    </div>
                    <input
                        ref={searchRef}
                        className="a12-q"
                        value={filters.q}
                        onChange={(e) => patch({ q: e.target.value })}
                        placeholder={t('/  ref, artikl, partner', '/  ref, item, partner')}
                    />
                    <button type="button" className="a12-iconbtn" onClick={() => setFresh((n) => n + 1)}>R</button>
                    <button
                        type="button"
                        className="a12-iconbtn"
                        disabled={exporting}
                        onClick={() => {
                            setExporting(true);
                            downloadFile(`/ado-1112/export?${qs}`, token, `m1112-${filters.dateFrom}-${filters.dateTo}.xlsx`)
                                .catch((e: Error) => setErr(e.message))
                                .finally(() => setExporting(false));
                        }}
                    >XLSX</button>
                    <button type="button" className="a12-iconbtn" onClick={onExit}>ESC</button>
                    <div className="a12-clock"><em>CLK</em>{fmtClock(now, locale)}</div>
                </div>
                <div className="a12-keys">1-5 {t('leće', 'lenses')} · QWE {t('tokovi', 'flows')} · G grain · T {t('danas', 'today')} · / {t('traka', 'search')} · R reload · ESC</div>
            </header>

            <div className="a12-quote">
                <div className="a12-qcell">
                    <div className="k">SPOT €/g</div>
                    <div className="v">{spot != null ? nfEur2.format(spot) : '—'}</div>
                    <div className="s">{t('u trenutku prihvata', 'at acceptance')}</div>
                </div>
                <div className="a12-qcell">
                    <div className="k">FIXING €/g</div>
                    <div className="v">{fixing != null ? nfEur2.format(fixing) : '—'}</div>
                    <div className="s">Argor {t('u rasponu', 'in range')}</div>
                </div>
                <div className="a12-qcell">
                    <div className="k">{t('REALIZIRANO', 'REALIZED')} €/g</div>
                    <div className="v">{realized != null ? nfEur2.format(realized) : '—'}</div>
                    <div className="s">{fmtPct(spread)} vs spot</div>
                </div>
                <div className="a12-qcell">
                    <div className="k">{t('MASA OUT', 'MASS OUT')}</div>
                    <div className="v">{fmtKg(soldG)}</div>
                    <div className="s">{fmtOz(soldG)} · {fmtEur(soldEur, true)}</div>
                </div>
                <div className="a12-qcell">
                    <div className="k">{t('MASA IN', 'MASS IN')}</div>
                    <div className="v">{fmtKg(boughtG)}</div>
                    <div className="s">{fmtOz(boughtG)} · {fmtEur(boughtEur, true)}</div>
                </div>
                <div className={'a12-qcell ' + (txnN ? '' : '')}>
                    <div className="k">{t('TXN / GRAIN', 'TXN / GRAIN')}</div>
                    <div className="v">{txnN}</div>
                    <div className="s">{filters.grain === 'offer' ? t('ponuda = BIS txn', 'offer = BIS txn') : t('artikl = stavka', 'article = line')}</div>
                </div>
            </div>

            <div className="a12-body">
                <ScopePlate meta={meta} filters={filters} set={patch} />
                <div className="a12-main">
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 260px', gap: 12 }}>
                        <div className="a12-ingots">
                            {ALL.map((ty) => {
                                const k = kpi(ty);
                                return (
                                    <GoldIngot
                                        key={ty}
                                        type={ty}
                                        active={filters.types.includes(ty)}
                                        onClick={() => toggleType(ty)}
                                        kgLabel={k ? fmtKg(k.fine_g) : '—'}
                                        ozLabel={k ? fmtOz(k.fine_g) : ''}
                                        eurLabel={k ? fmtEur(k.eur, true) : '—'}
                                        count={`${locale === 'hr' ? TYPE_HR[ty] : TYPE_EN[ty]} · ${k ? k.broj : '—'}`}
                                        spread={fmtPct(k?.spread_pct)}
                                        rucSpot={k?.ruc_spot_eur != null ? fmtEur(k.ruc_spot_eur, true) : '—'}
                                        rucFix={k?.ruc_fixing_eur != null ? fmtEur(k.ruc_fixing_eur, true) : '—'}
                                    />
                                );
                            })}
                        </div>
                        <div className="a12-side">
                            <div className="a12-plate">
                                <NetTank soldG={soldG} boughtG={boughtG} />
                            </div>
                            <div className="a12-plate">
                                <TripleQuote realized={realized} spot={spot} fixing={fixing} />
                            </div>
                        </div>
                    </div>

                    <section className="a12-plate">
                        <div className="a12-viz-h">
                            {LENSES.map((k, i) => (
                                <button key={k} type="button" className={'a12-chip' + (lens === k ? ' is-on' : '')} onClick={() => { setLens(k); setPick(null); }}>
                                    {i + 1} {locale === 'hr' ? LENS_HR[k] : LENS_EN[k]}
                                </button>
                            ))}
                            <span className="a12-cache">
                                {cockpit?._cache ? `${t('podaci', 'data')} ${isoToHr(cockpit._cache.cached_at.slice(0, 10))} ${cockpit._cache.cached_at.slice(11, 16)}${cockpit._cache.hit ? ' · cache' : ''}` : ''}
                            </span>
                        </div>
                        {err ? <div className="a12-err">{err}</div> : null}
                        {loading && !cockpit ? <div className="a12-load">{t('ASSAY IN PROGRESS', 'ASSAY IN PROGRESS')}</div> : null}
                        {lens === 'toka' && cockpit ? (
                            <HatchArea
                                labels={cockpit.spark.labels}
                                series={cockpit.spark.series}
                                types={filters.types}
                                formatLabel={(s) => isoToHr(s)}
                                onPick={(_i, label, values) => setPick(
                                    <Readout onClose={() => setPick(null)}>
                                        <div>{isoToHr(label)} · {fmtEur(ALL.filter((ty) => filters.types.includes(ty)).reduce((s, ty) => s + (values[ty] ?? 0), 0))}</div>
                                        {ALL.filter((ty) => filters.types.includes(ty)).map((ty) => (
                                            <div key={ty}>{locale === 'hr' ? TYPE_HR[ty] : TYPE_EN[ty]} · {fmtEur(values[ty] ?? 0)}</div>
                                        ))}
                                    </Readout>,
                                )}
                            />
                        ) : null}
                        {lens === 'teren' ? (
                            <RaceBars
                                rows={raceRows}
                                onPick={(key) => {
                                    const r = cockpit?.terrain.rows.find((x) => x.branch === key);
                                    setPick(
                                        <Readout onClose={() => setPick(null)}>
                                            <div>{key}</div>
                                            <div>{fmtEur(r?.metrics.ukupno?.eur ?? 0)} · {fmtKg(r?.metrics.ukupno?.fine_g ?? 0)} · {r?.metrics.ukupno?.broj ?? 0} txn</div>
                                            {ALL.map((ty) => <div key={ty}>{locale === 'hr' ? TYPE_HR[ty] : TYPE_EN[ty]} · {fmtEur(r?.metrics[ty]?.eur ?? 0)}</div>)}
                                        </Readout>,
                                    );
                                    patch({ branch: key });
                                }}
                                picked={filters.branch || null}
                            />
                        ) : null}
                        {lens === 'usporedba' ? (
                            <CompareLanes
                                labels={compareSeries.labels}
                                a={compareSeries.a}
                                b={compareSeries.b}
                                aLabel={t('ovo razdoblje', 'this period')}
                                bLabel={t('isti dani lani', 'same days last year')}
                                onPick={(i, label) => setPick(
                                    <Readout onClose={() => setPick(null)}>
                                        <div>{label}</div>
                                        <div>A {fmtEur(compareSeries.a[i] ?? 0)}</div>
                                        <div>B {fmtEur(compareSeries.b[i] ?? 0)}</div>
                                    </Readout>,
                                )}
                            />
                        ) : null}
                        {lens === 'sati' ? (
                            <HeatGrid
                                cells={cockpit?.heat.rows ?? []}
                                types={filters.types}
                                metric="eur"
                                onPick={(dow, hour, value) => setPick(
                                    <Readout onClose={() => setPick(null)}>
                                        <div>{t('dan', 'day')} {dow + 1} · {String(hour).padStart(2, '0')}:00</div>
                                        <div>{fmtEur(value)}</div>
                                    </Readout>,
                                )}
                            />
                        ) : null}
                        {lens === 'smjesa' && cockpit ? (
                            <MixPlate
                                parts={filters.types.map((ty) => {
                                    const p = cockpit.mix.find((x) => x.key === ty);
                                    return { key: ty, eur: p?.eur ?? 0, fine_g: p?.fine_g ?? 0, broj: p?.broj ?? 0 };
                                })}
                                onPick={(ty) => {
                                    const p = cockpit.mix.find((x) => x.key === ty);
                                    setPick(
                                        <Readout onClose={() => setPick(null)}>
                                            <div>{locale === 'hr' ? TYPE_HR[ty] : TYPE_EN[ty]}</div>
                                            <div>{fmtEur(p?.eur ?? 0)} · {fmtKg(p?.fine_g ?? 0)} · {p?.broj ?? 0}</div>
                                        </Readout>,
                                    );
                                    toggleType(ty);
                                }}
                            />
                        ) : null}
                        {pick}
                    </section>

                    <Tape
                        rows={tapeRows}
                        locale={locale}
                        total={tapeTotal}
                        loading={loading}
                        note={cockpit?.tape.note}
                        onOpen={(r) => { if (r.id > 0) setDetail({ type: r.type, id: r.id }); }}
                        onMore={loadMore}
                        hasMore={tapeRows.length < tapeTotal}
                    />
                </div>
            </div>
            {ticker ? (
                <div className="a12-ticker" aria-hidden>
                    <i>{ticker}    ·    {ticker}</i>
                </div>
            ) : null}
            {detail ? <Detail token={token} locale={locale} target={detail} onClose={() => setDetail(null)} /> : null}
        </div>
    );
}
